import { cn } from "@/lib/utils";

interface PulseMarkProps {
  size?: "sm" | "md" | "lg";
  variant?: "icon" | "lockup";
  animated?: boolean;
  className?: string;
}

const ICON_PX = { sm: 16, md: 22, lg: 28 } as const;
const TEXT_CLASS = {
  sm: "text-[11px]",
  md: "text-[13px]",
  lg: "text-[15px]",
} as const;

export function PulseMark({
  size = "md",
  variant = "icon",
  animated = false,
  className,
}: PulseMarkProps) {
  const px = ICON_PX[size];

  const icon = (
    <svg
      width={px}
      height={px}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="shrink-0"
    >
      <defs>
        <linearGradient id="pulse-mark-gradient" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="var(--pulse-accent-1)" />
          <stop offset="55%" stopColor="var(--pulse-accent-2)" />
          <stop offset="100%" stopColor="var(--pulse-accent-3)" />
        </linearGradient>
      </defs>
      {/* Baseline */}
      <line
        x1="2"
        y1="12"
        x2="22"
        y2="12"
        stroke="currentColor"
        strokeOpacity="0.35"
        strokeWidth="1.25"
        strokeLinecap="round"
      />
      {/* Short bar */}
      <line
        x1="6"
        y1="9"
        x2="6"
        y2="15"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      {/* Tall accent bar */}
      <line
        x1="12"
        y1="3.5"
        x2="12"
        y2="20.5"
        stroke="url(#pulse-mark-gradient)"
        strokeWidth="2.25"
        strokeLinecap="round"
        className={animated ? "animate-pulse-mark-beat" : undefined}
      />
      {/* Mid bar */}
      <line
        x1="18"
        y1="7"
        x2="18"
        y2="17"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );

  if (variant === "icon") {
    return <span className={cn("inline-flex", className)}>{icon}</span>;
  }

  return (
    <span className={cn("inline-flex items-center gap-2.5", className)}>
      {icon}
      <span
        className={cn(
          "font-display font-semibold uppercase tracking-[0.18em] text-foreground",
          TEXT_CLASS[size],
        )}
      >
        PULSE
      </span>
    </span>
  );
}
